/* Shared right-hand panels: price, preflight, sheet stats. */

import { useMemo } from 'react';
import { preflight, priceProject, priceSheets, requestedCount, formatMoney, formatDims, formatMetres, sheetStats, needsAcknowledgement, type PreflightResult, type Issue, type SheetForPricing } from '@gsb/core';
import { useStore, build, acknowledge, removeDesigns, commit, fitInside, select, setActiveSheet, estimateLengthMm, removeSheet } from '../store/store';
import { Icon } from './icons';

export function usePreflight(): PreflightResult | null {
  const doc = useStore(s => s.doc);
  const assets = useStore(s => s.assets);
  const cfg = useStore(s => s.config);
  return useMemo(() => (cfg ? preflight(doc, assets, cfg.builder, cfg.pricing as any) : null), [doc, assets, cfg]);
}

export function usePrice() {
  const doc = useStore(s => s.doc);
  const cfg = useStore(s => s.config);
  return useMemo(() => (cfg && doc.sheets.length ? priceProject(doc, cfg.pricing as any) : null), [doc, cfg]);
}

/** The price to show: the real one once built (and up to date), else an estimate from the design list. */
export function useEstimate(): { total: number; built: boolean; lengthMm: number | null; tierPct: number } | null {
  const doc = useStore(s => s.doc);
  const cfg = useStore(s => s.config);
  return useMemo(() => {
    if (!cfg) return null;
    if (doc.sheets.length && !doc.layoutStale && doc.sheets.some(s => s.items.length)) {
      const p = priceProject(doc, cfg.pricing as any);
      return { total: p.total, built: true, lengthMm: p.lengthMetres * 1000, tierPct: p.tier ? p.tier.percentOff : 0 };
    }
    if (!requestedCount(doc)) return null;
    const tp = cfg.pricing.transfers[doc.transfer];
    const L = estimateLengthMm(doc, cfg);
    const c = doc.settings.sheet;
    const maxL = tp.byLength.maxLengthMm;
    let sheets: SheetForPricing[];
    if (c.mode === 'fixed') sheets = [{ kind: 'fixed', fixedKey: c.key, widthMm: 0, lengthMm: 0 }];
    else if (c.mode === 'length') sheets = Array.from({ length: Math.max(1, Math.ceil(L / c.lengthMm)) }, () => ({ kind: 'length' as const, widthMm: tp.byLength.widthMm, lengthMm: c.lengthMm }));
    else {
      const n = Math.max(1, Math.ceil(L / maxL));
      sheets = Array.from({ length: n }, (_v, i) => ({ kind: 'roll' as const, widthMm: tp.byLength.widthMm, lengthMm: i < n - 1 ? maxL : L - maxL * (n - 1) }));
    }
    const p = priceSheets(sheets, doc.transfer, cfg.pricing as any);
    return { total: p.total, built: false, lengthMm: L, tierPct: p.tier ? p.tier.percentOff : 0 };
  }, [doc, cfg]);
}

/** item id → error/warning, for outlines on the canvas */
export function useFlags(pf: PreflightResult | null) {
  return useMemo(() => {
    const m = new Map<string, 'error' | 'warning'>();
    pf?.issues.forEach(i => {
      if (i.level === 'info' || !i.itemIds) return;
      i.itemIds.forEach(id => { if (m.get(id) !== 'error') m.set(id, i.level as 'error' | 'warning'); });
    });
    return m;
  }, [pf]);
}

export function PriceCard() {
  const p = usePrice();
  const doc = useStore(s => s.doc);
  const cfg = useStore(s => s.config)!;
  if (!p) return null;
  const tiers = cfg.pricing.transfers[doc.transfer].tiers;
  const hasLength = doc.sheets.some(s => s.kind !== 'fixed');
  const useBetter = () => {
    if (!p.better) return;
    const L = p.better.lengthMm;
    commit('Use a ' + L / 1000 + ' m sheet', d => {
      const s = d.sheets.find(x => x.kind !== 'fixed');
      if (s) { s.kind = 'length'; s.lengthMm = L; }
      d.settings.sheet = { mode: 'length', lengthMm: L };
    });
  };
  return (
    <div className="sec">
      <div className="sec-title"><span className="label">Estimated price</span><span className="chip">{cfg.pricing.taxLabel} incl.</span></div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <b className="num" style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-.02em' }}>{formatMoney(p.total)}</b>
        {p.saving > 0 && <span className="chip chip-green">You save {formatMoney(p.saving)}</span>}
      </div>
      <ul style={{ marginTop: 8, display: 'grid', gap: 4 }}>
        {p.lines.map(l => (
          <li key={l.index} className="small" style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
            <span className="muted">Sheet {l.index + 1} · {l.name}{l.ratePerMetre ? ' @ ' + formatMoney(l.ratePerMetre) + '/m' : ''}</span>
            <b className="num">{formatMoney(l.price)}</b>
          </li>
        ))}
      </ul>
      {hasLength && tiers.length > 0 && (
        <>
          <span className="label" style={{ margin: '14px 0 6px' }}>Bulk discount · {formatMetres(p.lengthMetres * 1000)} in this order</span>
          <ul className="tiers">
            {[...tiers].sort((a, b) => a.fromMetres - b.fromMetres).map(t => (
              <li key={t.fromMetres} className={p.tier && p.tier.fromMetres === t.fromMetres ? 'is-on' : ''}><b>{Math.round(t.percentOff * 100)}%</b>{t.fromMetres} m+</li>
            ))}
          </ul>
        </>
      )}
      {p.better && (
        <div className="callout callout-info" style={{ marginTop: 12 }}>
          <Icon name="sparkle" />
          <div>
            <b>Longer is cheaper here.</b> A {p.better.lengthMm / 1000} m sheet costs {formatMoney(p.better.total)}, {formatMoney(p.better.saving)} less, and gives you room for more designs.
            <div style={{ marginTop: 8 }}><button className="btn btn-primary btn-xs" onClick={useBetter}>Use {p.better.lengthMm / 1000} m</button></div>
          </div>
        </div>
      )}
      {!p.better && p.nextTier && hasLength && p.nextTier.extraMetres > 0 && p.nextTier.extraMetres < 3 && (
        <p className="tiny muted" style={{ marginTop: 10 }}>Add {formatMetres(p.nextTier.extraMetres * 1000)} more and the rate drops to {formatMoney(p.nextTier.ratePerMetre)}/m.</p>
      )}
      <p className="tiny muted" style={{ marginTop: 10 }}>Choose standard or rush turnaround when you order. We confirm the final price before anything is charged.</p>
    </div>
  );
}

export function SheetsCard() {
  const doc = useStore(s => s.doc);
  const active = useStore(s => s.activeSheet);
  const unit = doc.settings.unit;
  const all = doc.sheets.map(sheetStats);
  const film = all.reduce((a, s) => a + s.filmMm2, 0), art = all.reduce((a, s) => a + s.artMm2, 0);
  const pct = film ? Math.round((art / film) * 100) : 0;
  return (
    <div className="sec">
      <div className="util">
        <span className="util-ring" style={{ ['--p' as any]: pct }}><span className="num">{pct}%</span></span>
        <div>
          <b style={{ display: 'block', fontSize: 14.5 }}>Gangsheet optimised</b>
          <span className="small muted">{pct}% film utilisation · {all.reduce((n, s) => n + s.count, 0)} transfers</span>
        </div>
      </div>
      <ul style={{ display: 'grid', gap: 6, marginTop: 12 }}>
        {doc.sheets.map((s, i) => (
          <li key={s.id}>
            <button className="stab" style={{ width: '100%', justifyContent: 'space-between' }} aria-selected={i === active} onClick={() => setActiveSheet(i)}>
              <span>Sheet {i + 1} · {formatDims(s.widthMm, s.lengthMm, unit === 'mm' ? 'cm' : unit)}</span>
              <small>{s.items.length} · {Math.round(all[i].utilisation * 100)}%</small>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The sidebar's short version: status, then the first few things to fix. */
export function ChecksSummary({ onMore, max = 3 }: { onMore: () => void; max?: number }) {
  const pf = usePreflight();
  const doc = useStore(s => s.doc);
  if (!pf || !doc.sheets.length) return null;
  const problems = pf.issues.filter(i => (i.level === 'error' || i.level === 'warning'));
  return (
    <div className="checks">
      {pf.status === 'ready'
        ? <div className="pf-head ok"><Icon name="okCircle" />Ready to print</div>
        : <div className={'pf-head ' + (pf.status === 'blocked' ? 'bad' : 'warn')}><Icon name="alert" />{problems.length === 1 ? '1 thing to check' : problems.length + ' things to check'}</div>}
      {problems.slice(0, max).map(i => <IssueRow key={i.id} i={i} compact />)}
      {problems.length > max && <button className="link small" onClick={onMore}>See all {problems.length} in Review <Icon name="arrowRight" /></button>}
    </div>
  );
}

export function PreflightCard({ onEdit }: { onEdit?: () => void }) {
  const pf = usePreflight();
  const doc = useStore(s => s.doc);
  if (!pf) return null;
  const problems = pf.issues.filter(i => i.level !== 'info' || i.code === 'low_dpi' || i.code === 'fair_dpi' || i.code === 'opaque');
  const toFix = pf.errors + pf.warnings;
  return (
    <div className="sec">
      <span className="label">Ready to print?</span>
      <div className="pf">
        {pf.status === 'ready'
          ? <div className="pf-head ok"><Icon name="okCircle" />Ready to print</div>
          : pf.status === 'blocked'
            ? <div className="pf-head bad"><Icon name="alert" />{toFix === 1 ? 'There’s 1 thing to fix before we print this' : 'There are ' + toFix + ' things to check before we print this'}</div>
            : <div className="pf-head warn"><Icon name="alert" />{toFix === 1 ? '1 thing to check' : toFix + ' things to check'}</div>}
        {pf.status === 'ready' && <p className="small muted">Everything is inside the printable area, spaced {doc.settings.gapMm} mm apart, and sharp enough to print.</p>}
        {problems.map(i => <IssueRow key={i.id} i={i} onEdit={onEdit} />)}
      </div>
    </div>
  );
}

export function IssueRow({ i, onEdit, compact }: { i: Issue; onEdit?: () => void; compact?: boolean }) {
  const doc = useStore(s => s.doc);
  const ackKey = i.code === 'opaque' ? 'opaque:' + i.designId : 'dpi:' + i.designId;
  const acked = i.designId ? doc.acknowledged.includes(ackKey) : false;
  const show = () => {
    if (i.sheetIndex !== undefined) setActiveSheet(i.sheetIndex);
    if (i.itemIds) select(i.itemIds);
    else if (i.designId) {
      const s = doc.sheets.findIndex(sh => sh.items.some(it => it.designId === i.designId));
      if (s >= 0) { setActiveSheet(s); select(doc.sheets[s].items.filter(it => it.designId === i.designId).map(it => it.id)); }
    }
    onEdit?.();
  };
  const moveInside = () => commit('Move inside the sheet', d => {
    const s = d.sheets[i.sheetIndex ?? 0];
    if (!s) return;
    const cfg = useStore.getState().config!;
    s.items.forEach(it => {
      if (!i.itemIds?.includes(it.id)) return;
      // inside the printable area (clampInside keeps the edge margin; too big = smaller to fit)
      const p = fitInside(it, s, cfg.pricing.transfers[d.transfer].byLength.maxLengthMm);
      it.cx = p.cx; it.cy = p.cy; it.widthMm = p.widthMm; it.heightMm = p.heightMm;
    });
  });
  return (
    <div className={'pf-item ' + (acked ? 'info' : i.level)}>
      <Icon name={i.level === 'error' ? 'alert' : i.level === 'warning' && !acked ? 'alert' : 'info'} />
      <div>
        <b>{i.title}</b>
        {i.detail && !compact && <p>{i.detail}</p>}
      </div>
      <div className="acts">
        {i.fix === 'rebuild' && <button className="btn btn-soft btn-xs" onClick={() => void build(i.code === 'stale' || i.code === 'unplaced' || i.code === 'empty' ? 'build' : 'renest')}><Icon name="build" />{i.code === 'empty' ? 'Build' : 'Rebuild'}</button>}
        {(i.fix === 'acknowledge' || (acked && (i.code === 'low_dpi' || i.code === 'opaque'))) && i.designId && (
          <label className="switch" style={{ fontSize: 12.5 }}><input type="checkbox" checked={acked} onChange={e => acknowledge(ackKey, e.target.checked)} /><i />I understand, print it anyway</label>
        )}
        {i.fix === 'move_inside' && <button className="btn btn-soft btn-xs" onClick={moveInside}><Icon name="fit" />Move inside</button>}
        {(i.code === 'overlap' || i.code === 'too_close') && <button className="btn btn-soft btn-xs" onClick={() => void build('renest')}><Icon name="build" />Re-nest</button>}
        {(i.fix === 'select' || i.fix === 'move_inside' || i.code === 'low_dpi' || i.code === 'fair_dpi') && (i.itemIds || i.designId) && <button className="btn btn-soft btn-xs" onClick={show}><Icon name="eye" />Show me</button>}
        {i.fix === 'remove_design' && i.designId && <button className="btn btn-danger btn-xs" onClick={() => removeDesigns([i.designId!])}><Icon name="trash" />Remove</button>}
        {i.fix === 'remove_sheet' && i.sheetIndex !== undefined && <button className="btn btn-soft btn-xs" onClick={() => removeSheet(i.sheetIndex!)}><Icon name="trash" />Remove sheet</button>}
      </div>
    </div>
  );
}

export function canOrder(pf: PreflightResult | null, doc = useStore.getState().doc): { ok: boolean; why?: string } {
  if (!pf) return { ok: false };
  if (pf.errors) return { ok: false, why: 'Fix the items in red first.' };
  const need = needsAcknowledgement(pf).filter(i => !doc.acknowledged.includes((i.code === 'opaque' ? 'opaque:' : 'dpi:') + i.designId));
  if (need.length) return { ok: false, why: 'Tick “I understand” on the warnings first.' };
  return { ok: true };
}
